import { useCallback, useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { CampaignConfigSchema, MessageTemplate as MessageTemplateType } from '../types';

export const createDefaultTemplate = (): MessageTemplateType => ({
  id: `template-${Date.now()}`,
  name: '',
  messageType: 'sms-or-mms',
  currentMessageType: 'sms',
  smsText: '',
  mmsSubject: '',
  mmsText: '',
  schedule: { value: 'immediately', visible: true, scheduledType: 'immediate' },
  mmsFieldsVisibility: { subject: true, text: true, attachment: true },
  dataCaptureVisible: true,
  surveyVisible: false,
  scheduleVisible: true,
  oneMessageOnly: false,
  survey: { active: false },
});

const cloneTemplateForAdd = (template: MessageTemplateType): MessageTemplateType => ({
  ...template,
  id: `template-${Date.now()}`,
  schedule: template.schedule ? { ...template.schedule } : undefined,
  mmsFieldsVisibility: template.mmsFieldsVisibility ? { ...template.mmsFieldsVisibility } : undefined,
  dataCapture: template.dataCapture ? { ...template.dataCapture } : undefined,
  survey: template.survey ? { ...template.survey } : undefined,
});

type SetConfig = React.Dispatch<React.SetStateAction<CampaignConfigSchema>>;

/**
 * CRUD + reordering for message templates, operating on the shared config state.
 */
export const useMessageTemplates = (setConfig: SetConfig) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const addTemplate = useCallback(
    (section?: 'join' | 'followup') => {
      setConfig(prev => {
        let next: MessageTemplateType;
        if (section) {
          // Enrollment: a fresh template tagged for its section.
          next = { ...createDefaultTemplate(), messageSection: section };
        } else {
          const last = prev.messageTemplates[prev.messageTemplates.length - 1];
          next = last ? cloneTemplateForAdd(last) : createDefaultTemplate();
        }
        setSelectedTemplateId(next.id);
        return { ...prev, messageTemplates: [...prev.messageTemplates, next] };
      });
    },
    [setConfig],
  );

  const updateTemplate = useCallback(
    (id: string, updates: Partial<MessageTemplateType>) => {
      setConfig(prev => ({
        ...prev,
        messageTemplates: prev.messageTemplates.map(t => (t.id === id ? { ...t, ...updates } : t)),
      }));
    },
    [setConfig],
  );

  const deleteTemplate = useCallback(
    (id: string) => {
      setConfig(prev => ({
        ...prev,
        messageTemplates: prev.messageTemplates.filter(t => t.id !== id),
      }));
      setSelectedTemplateId(current => (current === id ? null : current));
    },
    [setConfig],
  );

  const reorder = useCallback(
    (activeId: string, overId: string) => {
      setConfig(prev => {
        const from = prev.messageTemplates.findIndex(t => t.id === activeId);
        const to = prev.messageTemplates.findIndex(t => t.id === overId);
        if (from === -1 || to === -1 || from === to) return prev;
        return { ...prev, messageTemplates: arrayMove(prev.messageTemplates, from, to) };
      });
    },
    [setConfig],
  );

  return { selectedTemplateId, setSelectedTemplateId, addTemplate, updateTemplate, deleteTemplate, reorder };
};
